import { useId } from 'react';

import { isGroupType, type AnswerDraft } from '@mds/shared/lib/form-answers';
import { cn } from '@mds/shared/lib/utils';
import type { FormQuestion } from '@mds/shared/types/form-question';
import { QuestionInput } from '@mds/shared/ui/form/question-input';

import Field, { FieldError, inputCls } from '@/components/ui/field';

/**
 * One question of an event form, in the admin's field skin.
 *
 * A one-box question sits in the usual Field. A row of options — radios,
 * checkboxes, the rating — is not one control, so a wrapping label would hand
 * every click to its first option; it gets a fieldset and legend drawn to
 * match instead.
 */
export default function QuestionField({
  question,
  value,
  onChange,
  error,
  onBlur,
  disabled,
}: {
  question: FormQuestion;
  value: AnswerDraft;
  onChange: (next: AnswerDraft) => void;
  error?: string;
  onBlur?: () => void;
  disabled?: boolean;
}) {
  const errorId = useId();

  if (!isGroupType(question.type)) {
    return (
      <Field label={question.label} required={question.required} error={error} onBlur={onBlur}>
        <QuestionInput
          question={question}
          value={value}
          onChange={onChange}
          disabled={disabled}
          boxClassName={inputCls}
        />
      </Field>
    );
  }

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
      <legend
        className={cn(
          'mb-2 p-0 font-mono text-sm font-semibold tracking-wider uppercase',
          error ? 'text-danger-text' : 'text-ink-secondary',
        )}
      >
        {question.label}
        {question.required && (
          <>
            <span className="text-danger-text" aria-hidden>
              {' *'}
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </legend>
      <QuestionInput
        question={question}
        value={value}
        onChange={onChange}
        disabled={disabled}
        onBlur={onBlur}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
      />
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </fieldset>
  );
}
